import { useState, useEffect, useMemo, useCallback } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import {
    RefreshCw, Search, ChevronDown, ChevronUp, Users, Target, Download,
    AlertTriangle, Save, X,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { TEAM_PERFORMANCE_URL, TEAM_TARGETS_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import dayjs from "dayjs";

// ── date presets ──────────────────────────────────────────────────────────────

const DATE_PRESETS = [
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last Month" },
    { value: "last_week",  label: "Last 7 Days" },
    { value: "last_90",    label: "Last 90 Days" },
    { value: "all",        label: "All Time" },
    { value: "custom",     label: "Custom Range" },
];

const resolveRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt = d => d.format("YYYY-MM-DD");
    switch (preset) {
        case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
        case "last_month": return {
            from_date: fmt(today.subtract(1, "month").startOf("month")),
            to_date:   fmt(today.subtract(1, "month").endOf("month")),
        };
        case "last_week": return { from_date: fmt(today.subtract(7, "day")),  to_date: fmt(today) };
        case "last_90":   return { from_date: fmt(today.subtract(90, "day")), to_date: fmt(today) };
        case "custom":    return (customFrom && customTo)
            ? { from_date: customFrom, to_date: customTo } : {};
        default:          return {};
    }
};

// ── columns ───────────────────────────────────────────────────────────────────
// `num` marks a metric column so header clicks sort numerically.

const COLUMNS = [
    { key: "name",              label: "Rep",            align: "left" },
    { key: "pipeline_open",     label: "Open",           num: true, hint: "Open leads assigned right now (not date-scoped)" },
    { key: "activities",        label: "Activities",     num: true, hint: "Status changes and notes logged in the period" },
    { key: "leads_touched",     label: "Leads Worked",   num: true, hint: "Distinct leads with at least one activity" },
    { key: "demos_fixed",       label: "Demos Fixed",    num: true },
    { key: "demos_done",        label: "Demos Done",     num: true },
    { key: "show_rate",         label: "Show %",         num: true, hint: "Demos done ÷ demos fixed" },
    { key: "won",               label: "Won",            num: true },
    { key: "lost",              label: "Lost",           num: true },
    { key: "conversion_rate",   label: "Conv %",         num: true, hint: "Won ÷ leads worked" },
    { key: "revenue_won",       label: "Revenue",        num: true },
    { key: "overdue_followups", label: "Overdue",        num: true, hint: "Open leads whose promised follow-up date has passed" },
];

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

// Colour the rate columns so weak numbers read at a glance
const rateTone = (v, good, ok) => {
    if (v == null) return "text-gray-400 dark:text-gray-500";
    if (v >= good) return "text-emerald-600 dark:text-emerald-400 font-semibold";
    if (v >= ok)   return "text-amber-600 dark:text-amber-400";
    return "text-rose-600 dark:text-rose-400";
};

// ── target vs actual bar ──────────────────────────────────────────────────────

const TargetBar = ({ actual, target, format }) => {
    if (target == null || target === 0) {
        return <span className="text-[10px] text-gray-300 dark:text-gray-600">not set</span>;
    }
    const pct = Math.min(100, Math.round(100 * (actual || 0) / target));
    const hit = (actual || 0) >= target;
    return (
        <div className="min-w-[92px]">
            <div className="flex items-baseline justify-between gap-1 mb-0.5">
                <span className="text-[10px] font-medium text-gray-700 dark:text-gray-200 tabular-nums">
                    {format ? format(actual) : actual}
                </span>
                <span className="text-[9px] text-gray-400 tabular-nums">
                    / {format ? format(target) : target}
                </span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div className={cn("h-full rounded-full", hit ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-rose-500")}
                    style={{ width: `${Math.max(pct, 2)}%` }} />
            </div>
        </div>
    );
};

// ── main component ────────────────────────────────────────────────────────────

const TeamPerformanceReport = () => {
    const { fetch, create } = useApiMethods();
    // Matches how the rest of the app reads the role (see use-navigation.js).
    // Only managers may set targets; the backend enforces the same rule.
    const isManager = sessionStorage.getItem("login_role") !== "user";

    const [rows,    setRows]    = useState([]);
    const [totals,  setTotals]  = useState(null);
    const [month,   setMonth]   = useState(null);
    const [loading, setLoading] = useState(false);
    const [search,  setSearch]  = useState("");

    const [preset,     setPreset]     = usePersistedState("team_perf_preset", "this_month");
    const [customFrom, setCustomFrom] = usePersistedState("team_perf_from", "");
    const [customTo,   setCustomTo]   = usePersistedState("team_perf_to", "");
    const [showInactive, setShowInactive] = usePersistedState("team_perf_inactive", false);
    const [sortKey,    setSortKey]    = usePersistedState("team_perf_sort", "won");
    const [sortAsc,    setSortAsc]    = usePersistedState("team_perf_asc", false);

    const [editing,  setEditing]  = useState(null);   // user_id being edited
    const [draft,    setDraft]    = useState({});
    const [saving,   setSaving]   = useState(false);

    const load = useCallback(() => {
        if (preset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveRange(preset, customFrom, customTo);
        const params = new URLSearchParams();
        if (from_date) params.append("from_date", from_date);
        if (to_date)   params.append("to_date",   to_date);
        const qs = params.toString();

        fetch(qs ? `${TEAM_PERFORMANCE_URL}?${qs}` : TEAM_PERFORMANCE_URL)
            .then(res => {
                if (res?.status === "success") {
                    setRows(Array.isArray(res.data) ? res.data : []);
                    setTotals(res.totals || null);
                    setMonth(res.month || null);
                } else {
                    ToastMsg("error", res?.message || "Failed to load team performance");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load team performance"))
            .finally(() => setLoading(false));
    }, [preset, customFrom, customTo]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const visible = useMemo(() => {
        let out = rows;
        if (!showInactive) out = out.filter(r => r.is_active_user);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            out = out.filter(r => r.name?.toLowerCase().includes(q) ||
                                  (r.emp_code || "").toLowerCase().includes(q));
        }
        return [...out].sort((a, b) => {
            const av = a[sortKey], bv = b[sortKey];
            if (sortKey === "name") {
                return sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
            }
            const an = av == null ? -1 : av, bn = bv == null ? -1 : bv;
            return sortAsc ? an - bn : bn - an;
        });
    }, [rows, showInactive, search, sortKey, sortAsc]);

    const toggleSort = (key) => {
        if (sortKey === key) setSortAsc(!sortAsc);
        else { setSortKey(key); setSortAsc(key === "name"); }
    };

    const startEdit = (row) => {
        setEditing(row.user_id);
        setDraft({
            demos_target:    row.demos_target ?? "",
            closures_target: row.closures_target ?? "",
            revenue_target:  row.revenue_target ?? "",
        });
    };

    const saveTarget = (row) => {
        setSaving(true);
        create(TEAM_TARGETS_URL, {
            user_id: row.user_id,
            month,
            demos_target:    Number(draft.demos_target)    || 0,
            closures_target: Number(draft.closures_target) || 0,
            revenue_target:  Number(draft.revenue_target)  || 0,
        })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", `Target saved for ${row.name}`);
                    setEditing(null);
                    load();
                } else {
                    ToastMsg("error", res?.message || "Failed to save target");
                }
            })
            .catch(() => ToastMsg("error", "Failed to save target"))
            .finally(() => setSaving(false));
    };

    const exportExcel = () => {
        const { from_date, to_date } = resolveRange(preset, customFrom, customTo);
        const params = new URLSearchParams({ export_type: "excel" });
        if (from_date) params.append("from_date", from_date);
        if (to_date)   params.append("to_date",   to_date);
        window.open(`${process.env.REACT_APP_API_URL || ""}${TEAM_PERFORMANCE_URL}?${params}`, "_blank");
    };

    const inactiveCount = rows.filter(r => !r.is_active_user).length;

    return (
        <ContainerCard>
            {/* ── header ── */}
            <div className="flex-shrink-0">
                <div className="flex items-center justify-between mb-4 px-1 gap-3 flex-wrap">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <Users className="h-4 w-4 text-indigo-500" />
                            Sales Team Performance
                        </h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Every rep side by side — activity, demo show-rate, wins and monthly targets
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={exportExcel} size="sm" variant="outline"
                            className="flex items-center gap-1 text-xs" disabled={loading || !rows.length}>
                            <Download className="h-3 w-3" /> Excel
                        </Button>
                        <Button onClick={load} size="sm" variant="outline"
                            className="flex items-center gap-1 text-xs" disabled={loading}>
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                        </Button>
                    </div>
                </div>

                {/* ── filters ── */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="relative">
                        <select value={preset} onChange={e => {
                            setPreset(e.target.value);
                            if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); }
                        }}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer">
                            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    {preset === "custom" && (
                        <>
                            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none" />
                            <span className="text-xs text-gray-400">to</span>
                            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none" />
                        </>
                    )}

                    {inactiveCount > 0 && (
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                            <input type="checkbox" checked={showInactive}
                                onChange={e => setShowInactive(e.target.checked)}
                                className="rounded border-gray-300 dark:border-gray-600" />
                            Include past reps ({inactiveCount})
                        </label>
                    )}

                    <div className="ml-auto flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 bg-white dark:bg-gray-800">
                        <Search className="h-3 w-3 text-gray-400" />
                        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Rep name or code…"
                            className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 w-36" />
                    </div>
                </div>

                {/* ── team totals ── */}
                {totals && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
                        {[
                            { label: "Reps",         value: visible.length },
                            { label: "Open Pipeline", value: totals.pipeline_open },
                            { label: "Activities",   value: totals.activities },
                            { label: "Demos",        value: `${totals.demos_done}/${totals.demos_fixed}`,
                              sub: totals.show_rate != null ? `${totals.show_rate}% show` : null },
                            { label: "Won",          value: totals.won, sub: `${totals.lost} lost` },
                            { label: "Revenue",      value: inr(totals.revenue_won) },
                        ].map(card => (
                            <div key={card.label}
                                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{card.label}</p>
                                <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{card.value}</p>
                                {card.sub && <p className="text-[10px] text-gray-400">{card.sub}</p>}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── table ── */}
            {loading ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading team performance…</p>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="flex-1 min-h-0 overflow-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                    {COLUMNS.map(c => (
                                        <th key={c.key} title={c.hint}
                                            onClick={() => toggleSort(c.key)}
                                            className={cn(
                                                "px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap cursor-pointer select-none hover:text-indigo-600 dark:hover:text-indigo-400",
                                                c.align === "left" ? "text-left" : "text-right"
                                            )}>
                                            <span className="inline-flex items-center gap-1">
                                                {c.label}
                                                {sortKey === c.key && (sortAsc
                                                    ? <ChevronUp className="h-3 w-3" />
                                                    : <ChevronDown className="h-3 w-3" />)}
                                            </span>
                                        </th>
                                    ))}
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                        <span className="inline-flex items-center gap-1">
                                            <Target className="h-3 w-3" /> Demos vs Target
                                        </span>
                                    </th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">Closures vs Target</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">Revenue vs Target</th>
                                    {isManager && <th className="px-3 py-2.5" />}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {visible.length === 0 ? (
                                    <tr>
                                        <td colSpan={COLUMNS.length + (isManager ? 4 : 3)}
                                            className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">
                                            No reps with activity in this period
                                        </td>
                                    </tr>
                                ) : visible.map(row => {
                                    const isEditing = editing === row.user_id;
                                    return (
                                        <tr key={row.user_id}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                                            <td className="px-3 py-2.5">
                                                <div className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                                    {row.name}
                                                    {!row.is_active_user && (
                                                        <span title="This user is no longer active"
                                                            className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                                                            past
                                                        </span>
                                                    )}
                                                </div>
                                                {row.emp_code && (
                                                    <div className="text-[10px] text-gray-400">{row.emp_code}</div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600 dark:text-gray-300">{row.pipeline_open}</td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600 dark:text-gray-300">{row.activities.toLocaleString()}</td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600 dark:text-gray-300">{row.leads_touched}</td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600 dark:text-gray-300">{row.demos_fixed}</td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-600 dark:text-gray-300">{row.demos_done}</td>
                                            <td className={cn("px-3 py-2.5 text-right tabular-nums", rateTone(row.show_rate, 50, 30))}>
                                                {row.show_rate == null ? "—"
                                                    : row.show_rate_cross_period
                                                        ? <span title="Some demos were fixed before this period and completed inside it, so this exceeds 100%">&gt;100%</span>
                                                        : `${row.show_rate}%`}
                                            </td>
                                            <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">{row.won}</td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-500 dark:text-gray-400">{row.lost}</td>
                                            <td className={cn("px-3 py-2.5 text-right tabular-nums", rateTone(row.conversion_rate, 5, 2))}>
                                                {row.conversion_rate == null ? "—" : `${row.conversion_rate}%`}
                                            </td>
                                            <td className="px-3 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-200">{inr(row.revenue_won)}</td>
                                            <td className="px-3 py-2.5 text-right tabular-nums">
                                                {row.overdue_followups > 0 ? (
                                                    <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 font-medium">
                                                        <AlertTriangle className="h-3 w-3" />{row.overdue_followups}
                                                    </span>
                                                ) : <span className="text-gray-300 dark:text-gray-600">0</span>}
                                            </td>

                                            {/* targets vs actuals */}
                                            {isEditing ? (
                                                <>
                                                    <td className="px-3 py-2.5">
                                                        <input type="number" min="0" value={draft.demos_target}
                                                            onChange={e => setDraft(d => ({ ...d, demos_target: e.target.value }))}
                                                            className="w-20 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                                                    </td>
                                                    <td className="px-3 py-2.5">
                                                        <input type="number" min="0" value={draft.closures_target}
                                                            onChange={e => setDraft(d => ({ ...d, closures_target: e.target.value }))}
                                                            className="w-20 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                                                    </td>
                                                    <td className="px-3 py-2.5">
                                                        <input type="number" min="0" value={draft.revenue_target}
                                                            onChange={e => setDraft(d => ({ ...d, revenue_target: e.target.value }))}
                                                            className="w-28 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                                                    </td>
                                                    <td className="px-3 py-2.5">
                                                        <div className="flex items-center gap-1">
                                                            <button disabled={saving} onClick={() => saveTarget(row)}
                                                                className="h-6 px-2 rounded-md text-[10px] font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white flex items-center gap-1">
                                                                <Save className="h-3 w-3" /> Save
                                                            </button>
                                                            <button onClick={() => setEditing(null)}
                                                                className="h-6 w-6 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center">
                                                                <X className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="px-3 py-2.5">
                                                        <TargetBar actual={row.demos_fixed} target={row.demos_target} />
                                                    </td>
                                                    <td className="px-3 py-2.5">
                                                        <TargetBar actual={row.won} target={row.closures_target} />
                                                    </td>
                                                    <td className="px-3 py-2.5">
                                                        <TargetBar actual={row.revenue_won} target={row.revenue_target} format={inr} />
                                                    </td>
                                                    {isManager && (
                                                        <td className="px-3 py-2.5">
                                                            <button onClick={() => startEdit(row)}
                                                                className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline whitespace-nowrap">
                                                                {row.demos_target == null ? "Set target" : "Edit"}
                                                            </button>
                                                        </td>
                                                    )}
                                                </>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>

                            {totals && visible.length > 0 && (
                                <tfoot className="sticky bottom-0">
                                    <tr className="bg-gray-100 dark:bg-gray-700 font-semibold text-gray-800 dark:text-gray-100">
                                        <td className="px-3 py-2">Team total</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{visible.reduce((s, r) => s + r.pipeline_open, 0)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{visible.reduce((s, r) => s + r.activities, 0).toLocaleString()}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{visible.reduce((s, r) => s + r.leads_touched, 0)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{visible.reduce((s, r) => s + r.demos_fixed, 0)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{visible.reduce((s, r) => s + r.demos_done, 0)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {(() => {
                                                const f = visible.reduce((s, r) => s + r.demos_fixed, 0);
                                                const d = visible.reduce((s, r) => s + r.demos_done, 0);
                                                return f ? `${Math.round(1000 * d / f) / 10}%` : "—";
                                            })()}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">{visible.reduce((s, r) => s + r.won, 0)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{visible.reduce((s, r) => s + r.lost, 0)}</td>
                                        <td className="px-3 py-2 text-right" />
                                        <td className="px-3 py-2 text-right tabular-nums">{inr(visible.reduce((s, r) => s + r.revenue_won, 0))}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{visible.reduce((s, r) => s + r.overdue_followups, 0)}</td>
                                        <td className="px-3 py-2" colSpan={isManager ? 4 : 3} />
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    {month && (
                        <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                            <p className="text-[10px] text-gray-400 dark:text-gray-500">
                                Targets shown for {dayjs(month).format("MMMM YYYY")}. "Open" is current pipeline;
                                every other column covers the selected period.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </ContainerCard>
    );
};

export default TeamPerformanceReport;
